import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useIdentity } from '@/entities/session';

import { initialsOf } from './initials';

/* Who is signed in: an initials square, the name, and the role beneath it.

 * **The name is on screen now rather than only in a `title`.** The chip was a 28px disc whose whole
 * content was two letters, with the name and roles in a tooltip — which is unreachable by touch and
 * unreachable by keyboard, on the one element that answers "am I signed in as the right person".
 * On a product where an admin and a reader see different controls on the same screen, that question
 * is asked often enough to deserve pixels. The `title` stays and still carries the full role list,
 * because the visible line shows the *primary* role only and a principal can hold several.
 *
 * **Read from `/v1/auth/me`, never from a token parsed in the browser.** The identity query is the
 * same one the Admin screens authorize against, so the name here and the permissions that decide
 * what a reader can see come from one answer — a chip built from a locally decoded JWT would keep
 * showing a name after the session behind it had been revoked.
 *
 * **The title carries the roles, and that is the point of the chip rather than decoration.** On a
 * product where the same screen shows different controls to a reader and an admin, "why can I not see
 * the activate button" is answered by knowing which roles you hold. The reference's own title is
 * `R. Menon · admin`, so it made the same call.
 *
 * **No initials are invented.** A `CurrentUser.display_name` falls back to the subject when there is
 * no local credential row, and a subject can be an opaque pairwise identifier from an IdP — so the
 * initials come out as whatever that string's first characters are, which is honest, rather than a
 * generated "U" that would be the same for everybody. While the read is in flight or has failed the
 * chip renders nothing at all: a placeholder disc is a claim that somebody is signed in, and a
 * failed identity read is the one case where that is exactly what is in doubt.
 */

export function UserChip(): ReactElement | null {
  const { t } = useTranslation();
  const identity = useIdentity();

  /* Nothing while unknown. See the note above: a disc with a letter in it asserts a signed-in
   * person, and that assertion is what a failed identity read cannot support. */
  if (identity.data === undefined) return null;

  const { display_name: displayName, roles } = identity.data;
  /* The first role, which is the one the visible line shows.
   *
   * **Not a join of all of them**, and not a count: the reference shows one word under the name, and
   * a header is the wrong place for "admin, approver, data-steward" — it wraps, it pushes the sign-out
   * control off a narrow viewport, and it is not what the reader is checking. The full list is on the
   * `title`, and `/team` is where somebody goes to read it properly.
   *
   * `roles.length === 0` renders its own sentence rather than an empty line, for the reason stated
   * below: a principal with no binding is denied everything, and that is the single most useful thing
   * this chip can say. */
  const primaryRole = roles[0];

  return (
    <span
      className="lq-userchip"
      /* `roles.length === 0` is its own sentence rather than an empty list. A principal with no role
       * binding is denied everything, and that is the single most useful thing the chip can say —
       * "nothing looks clickable" has an answer, and it is this. */
      title={
        roles.length === 0
          ? t('shell.user.noRoles', { name: displayName })
          : t('shell.user.roles', { name: displayName, roles: roles.join(', ') })
      }
    >
      {/* The initials are decorative: the visible name is beside them, so a screen reader
          announcing "RM" first would read the abbreviation as if it were a word and then read the
          name it abbreviates. */}
      <span className="lq-userchip__disc" aria-hidden="true">
        {initialsOf(displayName)}
      </span>
      {/* **The whole accessible name stays one hidden string, and the visible text is decorative.**
          My first version split it — a hidden "Signed in as" beside the rendered name — which reads
          correctly and is wrong twice over: it assembles a sentence out of two nodes, so a screen
          reader may pause between them, and it made the label depend on a copy string being
          interpolable with an empty name. Two shell tests caught it. One hidden label and an
          `aria-hidden` visible pair announces the sentence once and shows it once. */}
      <span className="lq-visually-hidden">
        {t('shell.user.label', { name: displayName })}
      </span>
      <span className="lq-userchip__who" aria-hidden="true">
        <span className="lq-userchip__name">{displayName}</span>
        <span className="lq-userchip__role">
          {primaryRole ?? t('shell.user.roleless')}
        </span>
      </span>
    </span>
  );
}
